import { useState } from "react";

import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";

import { Badge } from "@ps-design-system/components/Badge";
import { Button } from "@ps-design-system/components/Button";
import { Text } from "@ps-design-system/components/Text";

import { toggleBanner } from "../app-main/actions/banner";
import {
  useDeleteListingDatePricesMutation,
  useGetListingDatePricesQuery,
  useSaveListingDatePricesMutation,
} from "../services/date-prices.service";

import { toDateRates, toListingDatePrices } from "./DateRate.utils";
import { DateRateForm, type DateRateFormValues } from "./DateRateForm";
import { DateRateTable } from "./DateRateTable";

type DateRateProps = {
  currency: string;
  listingId: string;
  onFormDirtyChange: (isDirty: boolean) => void;
};

/**
 * Date based pricing section of the edit price page: rates that apply to
 * specific dates (holidays, busy seasons).
 */
const DateRate = ({
  currency,
  listingId,
  onFormDirtyChange,
}: DateRateProps) => {
  const dispatch = useDispatch();
  const [isDateRateFormOpen, setIsDateRateFormOpen] = useState(false);
  const { data: datePricesResponse } = useGetListingDatePricesQuery(
    { listingId },
    { skip: !listingId },
  );
  const [saveListingDatePrices] = useSaveListingDatePricesMutation();
  const [deleteListingDatePrices] = useDeleteListingDatePricesMutation();
  const dateRates = toDateRates(datePricesResponse?.date_prices ?? []);

  const handleAddDateRateClick = () => setIsDateRateFormOpen(true);
  const handleDateRateFormCancel = () => setIsDateRateFormOpen(false);
  const handleDateRateFormSubmit = async (dateRate: DateRateFormValues) => {
    try {
      await saveListingDatePrices({
        listingId,
        dates: toListingDatePrices(dateRate),
      }).unwrap();
      setIsDateRateFormOpen(false);
      dispatch(toggleBanner("save"));
    } catch {
      dispatch(toggleBanner("error"));
    }
  };
  const handleDateRateDelete = async (dates: string[]) => {
    try {
      await deleteListingDatePrices({ listingId, dates }).unwrap();
      dispatch(toggleBanner("save"));
    } catch {
      dispatch(toggleBanner("error"));
    }
  };

  return (
    <div>
      <div className="tw-flex tw-items-center tw-gap-12 tw-mb-8">
        <Text element="h4" typeface="headline-3-m" className="tw-m-0">
          <FormattedMessage
            id="hosting.date_rate.heading"
            defaultMessage="Date based pricing"
          />
        </Text>
        <Badge appearance="purple" size="sm">
          <FormattedMessage
            id="hosting.date_rate.new_badge"
            defaultMessage="NEW!"
          />
        </Badge>
      </div>
      <Text element="p" className="tw-m-0 tw-leading-28 tw-text-gray-600">
        <FormattedMessage
          id="hosting.date_rate.description"
          defaultMessage="Add rates based on specific dates (e.g. holidays, busy seasons)"
        />
      </Text>
      {dateRates.length > 0 && (
        <div className="tw-mt-24">
          <DateRateTable
            currency={currency}
            dateRates={dateRates}
            onDelete={handleDateRateDelete}
          />
        </div>
      )}
      <div className="tw-mt-24">
        {isDateRateFormOpen ? (
          <DateRateForm
            currency={currency}
            onCancel={handleDateRateFormCancel}
            onDirtyChange={onFormDirtyChange}
            onSubmit={handleDateRateFormSubmit}
          />
        ) : (
          <Button appearance="purple" onClick={handleAddDateRateClick}>
            <FormattedMessage
              id="hosting.date_rate.button_text_add_date_rate"
              defaultMessage="Add date based rate"
            />
          </Button>
        )}
      </div>
      <hr />
    </div>
  );
};

export default DateRate;
